/**
 * Notification Center — canonical unified surface (P3).
 *
 * Mounted under ME → Notifications so it lives inside the account
 * experience — not as a new top-level nav tab. Bell in the app header
 * opens this page.
 *
 * Deep-links: tapping a `message` category notification opens Connect;
 * conversations stay in Messenger. This screen is *never* a chat.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
  Bell, Check, Trash2, Loader2, ChevronLeft, Settings, ShieldAlert,
  Calendar, Plane, Wallet, ShoppingCart, Briefcase, MessageCircle,
  GraduationCap, Clock, Building2, Landmark, Siren, Lock, Tag, Info,
} from "lucide-react";
import { toast } from "sonner";

const CAT_ICON = {
  booking:    Calendar,     travel:     Plane,        payment:    Wallet,
  order:      ShoppingCart, lead:       Briefcase,    job:        Briefcase,
  message:    MessageCircle,family:     GraduationCap,reminder:   Clock,
  business:   Building2,    government: Landmark,     emergency:  Siren,
  security:   Lock,         offers:     Tag,
};

const CAT_COLOR = {
  emergency: "bg-red-100 text-red-700 border-red-200",
  security:  "bg-amber-100 text-amber-700 border-amber-200",
  payment:   "bg-emerald-100 text-emerald-700 border-emerald-200",
  booking:   "bg-sky-100 text-sky-700 border-sky-200",
  travel:    "bg-indigo-100 text-indigo-700 border-indigo-200",
  order:     "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  message:   "bg-teal-100 text-teal-700 border-teal-200",
  offers:    "bg-amber-100 text-amber-800 border-amber-200",
};

const PRIO_BADGE = {
  immediate: "bg-red-600 text-white",
  standard:  "bg-stone-200 text-stone-700",
  digest:    "bg-stone-100 text-stone-500",
};

export default function NotificationCenter() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy,  setBusy]  = useState(true);
  const [tab,   setTab]   = useState("all");     // all | important | unread
  const [cat,   setCat]   = useState("");        // category filter
  const [next,  setNext]  = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);

  const load = React.useCallback(async (cursor = null, replace = true) => {
    setBusy(true);
    try {
      const params = { limit: 30 };
      if (tab !== "all") params.filter = tab;
      if (cat) params.category = cat;
      if (cursor) params.cursor = cursor;
      const { data } = await api.get("/notifications", { params });
      setItems(replace ? (data.items || []) : [...items, ...(data.items || [])]);
      setNext(data.next_cursor || null);
    } catch (e) {
      toast.error(t("notifications.load_error", "Could not load notifications"));
    } finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, cat]);

  useEffect(() => { load(null, true); }, [load]);

  const markRead = async (id) => {
    try { await api.patch(`/notifications/${id}/read`); }
    catch { /* ignore */ }
    setItems(items.map(i => i.notif_id === id ? { ...i, read: true } : i));
  };
  const markAll = async () => {
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(t("notifications.marked_read", `Marked ${data.updated || 0} read`));
      setItems(items.map(i => ({ ...i, read: true })));
    } catch { toast.error(t("common.error", "Something went wrong")); }
  };
  const dismiss = async (id) => {
    try { await api.delete(`/notifications/${id}`); setItems(items.filter(i => i.notif_id !== id)); }
    catch { toast.error(t("common.error", "Something went wrong")); }
  };
  const onOpen = (n) => {
    if (!n.read) markRead(n.notif_id);
    if (n.action_url) nav(n.action_url);
  };

  const grouped = useMemo(() => items, [items]);
  const unread = useMemo(() => items.filter(i => !i.read).length, [items]);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="notification-center">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} className="text-stone-500 hover:text-stone-900" aria-label={t("common.back", "Back")} data-testid="notif-back">
            <ChevronLeft size={20}/>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">{t("me.section", "Account")}</div>
            <div className="font-black text-stone-900 text-base flex items-center gap-2">
              <Bell size={18}/> {t("notifications.title", "Notifications")}
              {unread > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-black" data-testid="notif-unread-count">{unread}</span>}
            </div>
          </div>
          <button onClick={() => setShowPrefs(true)} className="text-stone-500 hover:text-stone-900" aria-label={t("notifications.prefs", "Preferences")} data-testid="notif-prefs-btn">
            <Settings size={18}/>
          </button>
        </div>
        {/* Tabs */}
        <div className="max-w-3xl mx-auto px-4 pb-2 flex gap-2 overflow-x-auto">
          {[
            { k: "all",       lbl: t("notifications.tab.all",       "All") },
            { k: "important", lbl: t("notifications.tab.important", "Important") },
            { k: "unread",    lbl: t("notifications.tab.unread",    "Unread") },
          ].map(({ k, lbl }) => (
            <button key={k} onClick={() => setTab(k)}
                    data-testid={`notif-tab-${k}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-black border ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}>
              {lbl}
            </button>
          ))}
          <div className="w-px bg-stone-200 mx-1"/>
          <select value={cat} onChange={e => setCat(e.target.value)}
                  data-testid="notif-category-filter"
                  className="text-xs font-black rounded-full border border-stone-200 bg-white px-3 py-1.5">
            <option value="">{t("notifications.filter.all_cats", "All categories")}</option>
            {Object.keys(CAT_ICON).map(c =>
              <option key={c} value={c}>{t(`notifications.cat.${c}`, c.replace(/^./, ch => ch.toUpperCase()))}</option>
            )}
          </select>
          <button onClick={markAll} disabled={!items.some(i => !i.read)}
                  data-testid="notif-mark-all"
                  className="ml-auto text-xs font-black rounded-full bg-stone-100 hover:bg-stone-200 disabled:opacity-40 text-stone-700 px-3 py-1.5">
            <Check size={12} className="inline mr-1"/> {t("notifications.mark_all", "Mark all read")}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-2">
        {busy && !items.length && <div className="text-center py-10"><Loader2 size={20} className="animate-spin inline text-stone-400"/></div>}
        {!busy && !items.length && (
          <div className="text-center py-16 text-stone-400" data-testid="notif-empty">
            <Bell size={32} className="inline"/>
            <div className="mt-2 text-sm font-black">{t("notifications.empty", "You're all caught up")}</div>
            <div className="text-xs">{t("notifications.empty.sub", "Nothing new here right now.")}</div>
          </div>
        )}
        {grouped.map((n) => {
          const Icon = CAT_ICON[n.category] || Info;
          const colorCls = CAT_COLOR[n.category] || "bg-stone-100 text-stone-700 border-stone-200";
          const prioCls = PRIO_BADGE[n.priority] || PRIO_BADGE.standard;
          return (
            <div key={n.notif_id}
                 data-testid={`notif-row-${n.notif_id}`}
                 className={`rounded-2xl border p-3 flex gap-3 items-start ${n.read ? "bg-white border-stone-200" : "bg-white border-emerald-200 ring-1 ring-emerald-100"}`}>
              <div className={`w-10 h-10 rounded-xl border grid place-items-center flex-none ${colorCls}`}>
                <Icon size={18}/>
              </div>
              <button onClick={() => onOpen(n)} className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-black text-stone-500">
                    {t(`notifications.cat.${n.category}`, n.category)}
                  </span>
                  <span className={`text-[9px] px-1.5 rounded-full font-black ${prioCls}`}>
                    {t(`notifications.prio.${n.priority}`, n.priority)}
                  </span>
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>}
                </div>
                <div className="font-black text-stone-900 text-sm mt-0.5 truncate" data-testid={`notif-title-${n.notif_id}`}>{n.title}</div>
                {n.body && <div className="text-xs text-stone-600 mt-0.5 line-clamp-2">{n.body}</div>}
                <div className="text-[10px] text-stone-400 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
              </button>
              <button onClick={() => dismiss(n.notif_id)} className="text-stone-300 hover:text-red-600" aria-label={t("common.dismiss", "Dismiss")} data-testid={`notif-dismiss-${n.notif_id}`}>
                <Trash2 size={16}/>
              </button>
            </div>
          );
        })}
        {next && !busy && (
          <button onClick={() => load(next, false)} className="w-full py-3 rounded-2xl bg-white border border-stone-200 text-xs font-black text-stone-700" data-testid="notif-load-more">
            {t("common.load_more", "Load more")}
          </button>
        )}
      </div>

      {showPrefs && <PreferencesSheet onClose={() => setShowPrefs(false)}/>}
    </div>
  );
}

function PreferencesSheet({ onClose }) {
  const { t } = useI18n();
  const [prefs, setPrefs] = useState(null);
  const [critical, setCritical] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get("/notifications/preferences").then(r => {
      setPrefs(r.data.prefs); setCritical(r.data.critical || []);
      setBusy(false);
    }).catch(() => setBusy(false));
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/notifications/preferences", prefs);
      setPrefs(data.prefs);
      toast.success(t("notifications.prefs_saved", "Preferences saved"));
      onClose();
    } catch { toast.error(t("common.error", "Something went wrong")); }
  };
  const toggle = (channel, cat) => {
    if (critical.includes(cat)) {
      toast.info(t("notifications.critical_locked", "Emergency and Security cannot be disabled"));
      return;
    }
    setPrefs({ ...prefs, [channel]: { ...prefs[channel], [cat]: !prefs[channel][cat] } });
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-end md:items-center justify-center p-2" onClick={onClose} data-testid="notif-prefs-sheet">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 font-black text-stone-900">
          <ShieldAlert size={16}/> {t("notifications.prefs.title", "Notification preferences")}
        </div>
        <div className="text-[11px] text-stone-500">
          {t("notifications.prefs.hint", "Emergency and Security notifications are always on — we won't let a marketing setting mute a critical alert.")}
        </div>
        {busy || !prefs ? (
          <div className="text-center py-8"><Loader2 size={18} className="animate-spin inline"/></div>
        ) : (
          <div className="space-y-3">
            {Object.keys(CAT_ICON).map(cat => {
              const Icon = CAT_ICON[cat];
              const locked = critical.includes(cat);
              return (
                <div key={cat} className="rounded-xl border border-stone-200 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14}/>
                    <div className="text-xs font-black text-stone-900 flex-1">
                      {t(`notifications.cat.${cat}`, cat.replace(/^./, c => c.toUpperCase()))}
                    </div>
                    {locked && <span className="text-[9px] font-black text-red-600 uppercase" data-testid={`notif-critical-${cat}`}>{t("notifications.always_on", "Always on")}</span>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {["push", "email", "sms"].map(ch => (
                      <button key={ch} onClick={() => toggle(ch, cat)}
                              data-testid={`notif-pref-${ch}-${cat}`}
                              disabled={locked}
                              className={`text-[11px] font-black rounded-full px-3 py-1 border ${prefs[ch][cat] ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-stone-500 border-stone-200"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
                        {ch.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2">{t("common.cancel", "Cancel")}</button>
          <button onClick={save} disabled={busy} className="flex-1 rounded-full bg-emerald-600 text-white font-black text-xs py-2 disabled:opacity-50" data-testid="notif-prefs-save">
            {t("common.save", "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}
